import React, { useEffect, useState } from 'react';
import useCokitchenContext from 'contexts/cokitchen.context';
import { Input } from '@cokitchen/cokitchen-components';
import { getSimpleDateFormat } from 'utils/functions';
import PageHeaderText from 'components/base/page-header-text';
import PageText from 'components/base/page-text';
import { BrandType } from 'utils/types';
import { apiService } from 'services/base.service';
import useDashboardContext from 'contexts/dashboard.context';

const CreateCokitchenBrands = () => {
  const { cokitchens } = useDashboardContext();
  const { setCreateCokitchenForm, createCokitchenForm, setDisabled } = useCokitchenContext();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<BrandType[]>([]);

  useEffect(() => {
    fetchAllBrands();
  }, [cokitchens]);

  useEffect(() => {
    if (createCokitchenForm.brand_ids?.[0]) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
  }, [createCokitchenForm]);

  const fetchAllBrands = async () => {
    if (brands.length > 0) return;
    try {
      setLoading(true);
      const params = { is_fcb: false, item_type: 'FOOD' };
      const res = await apiService.get<BrandType[]>('brand_management/brands', {
        params,
        isInternal: true
      });
      const unarchivedBrands = res.data
        .filter(({ archived }) => !archived)
        .sort((a, b) => a.name.localeCompare(b.name));
      setBrands(unarchivedBrands);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (id: string) => {
    if (createCokitchenForm.brand_ids?.includes(id)) {
      setCreateCokitchenForm({
        ...createCokitchenForm,
        brand_ids: createCokitchenForm?.brand_ids?.filter((brand_id: string) => brand_id !== id)
      });
    } else {
      setCreateCokitchenForm({
        ...createCokitchenForm,
        brand_ids: [...createCokitchenForm.brand_ids, id]
      });
    }
  };

  return (
    <div>
      <PageHeaderText title='Create cokitchen' description='Add brands to the team' />
      {loading ? (
        <PageText text='Loading...' />
      ) : brands.length === 0 ? (
        <PageText text='No brands found' />
      ) : (
        <div className='grid grid-cols-[repeat(auto-fill,_minmax(230px,_1fr))] gap-5'>
          {brands.map((brand) => {
            return (
              <div
                key={brand.id}
                className={` flex flex-col relative hover:bg-[#F7F7F8] border border-1 z-0 rounded-[10px] border-[#EDEDEE] p-3 transition-height duration-1000`}>
                <div className='flex justify-between'>
                  <Input
                    type='checkbox'
                    name='new'
                    checkColor='red'
                    className='absolute top-3 left-3 z-[1000000000]'
                    checked={createCokitchenForm.brand_ids?.includes(brand.id)}
                    onChange={() => handleChange(brand.id)}
                  />
                </div>

                <div className='flex items-start justify-between gap-2'>
                  <img
                    className='w-16 h-16 rounded-full border border-[#E9ECEC] object-cover'
                    src={brand.logo}
                  />
                  <span className='text-sm leading-[19px] py-1 font-medium rounded-[100px] px-2 text-gray-5 bg-[#F2F2F3] truncate'>
                    {brand.cokitchen?.name ?? ''}
                  </span>
                </div>
                <p className='text-lg font-medium pt-2 pb-1'>{brand.name}</p>
                <p className='grow text-[#858585] text-xs leading-5'> {brand.description}</p>
                <hr className='my-3 text-[#DDDEDF]' />
                <p className='text-[10px] text-[#858585] pb-6'>
                  Added {getSimpleDateFormat(brand.updated_at ?? '')}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CreateCokitchenBrands;
